import { format as dfFormat, formatDistanceToNowStrict, parseISO } from "date-fns";
import { nb } from "date-fns/locale";

const nf = (min, max) => new Intl.NumberFormat("nb-NO", { minimumFractionDigits: min, maximumFractionDigits: max });

export const fmtNum = (v, decimals = 1) => (v === null || v === undefined || Number.isNaN(v) ? "–" : nf(decimals, decimals).format(v));
export const fmtInt = (v) => (v === null || v === undefined ? "–" : nf(0, 0).format(Math.round(v)));
export const fmtMW = (v, decimals = 1) => (v === null || v === undefined ? "–" : `${fmtNum(v, decimals)} MW`);
export const fmtMWh = (v) => {
  if (v === null || v === undefined) return "–";
  if (Math.abs(v) >= 10000) return `${fmtNum(v / 1000, 1)} GWh`;
  return `${fmtNum(v, v < 100 ? 1 : 0)} MWh`;
};
export const fmtNOK = (v) => {
  if (v === null || v === undefined) return "–";
  if (Math.abs(v) >= 1_000_000) return `${fmtNum(v / 1_000_000, 2)} mill. kr`;
  return `${fmtInt(v)} kr`;
};
export const fmtPct = (v, decimals = 1) => (v === null || v === undefined ? "–" : `${fmtNum(v, decimals)} %`);
export const fmtDelta = (v) => (v === null || v === undefined ? null : `${v > 0 ? "+" : ""}${fmtNum(v, 1)} %`);
export const fmtFlow = (v) => (v === null || v === undefined ? "–" : `${fmtNum(v, 2)} m³/s`);
export const fmtLevel = (v) => (v === null || v === undefined ? "–" : `${fmtNum(v, 2)} moh`);
export const fmtPrice = (v) => (v === null || v === undefined ? "–" : `${fmtNum(v / 10, 1)} øre/kWh`);
export const fmtHours = (v) => (v === null || v === undefined ? "–" : `${fmtNum(v, v % 1 === 0 ? 0 : 1)} t`);

const toDate = (d) => (typeof d === "string" ? parseISO(d) : d);

/**
 * All operational timestamps are shown in Norwegian time (Europe/Oslo), regardless of the browser's time zone –
 * the plants are in Norway and the hourly measurements are labelled in local time.
 */
const OSLO_DTF = new Intl.DateTimeFormat("en-US", { timeZone: "Europe/Oslo", hourCycle: "h23", year: "numeric", month: "numeric", day: "numeric", hour: "numeric", minute: "numeric", second: "numeric" });
export const toOslo = (d) => {
  const date = toDate(d);
  if (!date || Number.isNaN(date.getTime())) return date;
  const p = {};
  OSLO_DTF.formatToParts(date).forEach((part) => {
    if (part.type !== "literal") p[part.type] = parseInt(part.value, 10);
  });
  return new Date(p.year, p.month - 1, p.day, p.hour === 24 ? 0 : p.hour, p.minute, p.second);
};
/** Hour of day (0–23) in Norwegian time. */
export const osloHour = (d) => toOslo(d).getHours();

export const fmtDate = (d) => (d ? dfFormat(toOslo(d), "d. MMM yyyy", { locale: nb }) : "–");
export const fmtDateShort = (d) => (d ? dfFormat(toOslo(d), "d. MMM", { locale: nb }) : "–");
export const fmtDateTime = (d) => (d ? dfFormat(toOslo(d), "d. MMM yyyy 'kl.' HH.mm", { locale: nb }) : "–");
/** Norwegian clock notation: "18.00" (Språkrådet). Use as "kl. {fmtTime(d)}". */
export const fmtTime = (d) => (d ? dfFormat(toOslo(d), "HH.mm", { locale: nb }) : "–");
export const fmtDayTime = (d) => (d ? dfFormat(toOslo(d), "d. MMM 'kl.' HH.mm", { locale: nb }) : "–");
export const fmtRelative = (d) => (d ? formatDistanceToNowStrict(toDate(d), { addSuffix: true, locale: nb }) : "–");
export const fmtIsoDate = (d) => (d ? dfFormat(toDate(d), "yyyy-MM-dd") : "");

export const durationLabel = (hours) => {
  if (hours === null || hours === undefined) return "–";
  if (hours < 1) return `${Math.round(hours * 60)} min`;
  if (hours < 48) return `${fmtNum(hours, hours % 1 === 0 ? 0 : 1)} t`;
  return `${fmtNum(hours / 24, 1)} døgn`;
};
